import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { calculateCartSubtotal, clampProductQuantity, getCartStorageKey } from "../lib/cart";
import type { CartItem, StorefrontProduct } from "../types/storefront";
import { useBranch } from "./BranchContext";
import { useStorefront } from "./StorefrontContext";
import { useToast } from "./ToastContext";

interface CartState {
  items: CartItem[];
  addItem: (product: StorefrontProduct, quantity?: number) => void;
  removeItem: (productId: string) => void;
  incrementItem: (productId: string) => void;
  decrementItem: (productId: string) => void;
  setQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  revalidateCart: (products: StorefrontProduct[]) => void;
  subtotal: number;
  itemCount: number;
}

const CartContext = createContext<CartState | null>(null);

const readCart = (key: string) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? "[]");
    return Array.isArray(parsed) ? (parsed as CartItem[]) : [];
  } catch {
    localStorage.removeItem(key);
    return [];
  }
};

export function CartProvider({ children }: { children: React.ReactNode }) {
  const { slug } = useStorefront();
  const { branch } = useBranch();
  const { notify } = useToast();
  const storageKey = getCartStorageKey(slug ?? "unknown", branch?.id ?? "none");
  const [items, setItems] = useState<CartItem[]>([]);

  useEffect(() => setItems(readCart(storageKey)), [storageKey]);
  useEffect(() => localStorage.setItem(storageKey, JSON.stringify(items)), [items, storageKey]);

  const setQuantity = useCallback(
    (productId: string, rawQuantity: number) => {
      setItems((current) =>
        current.flatMap((item) => {
          if (item.product.id !== productId) return [item];
          if (rawQuantity <= 0) return [];
          const quantity = clampProductQuantity(item.product, rawQuantity);
          if (quantity !== rawQuantity) notify(`Quantity adjusted to ${quantity}.`, "info");
          return [{ ...item, quantity }];
        })
      );
    },
    [notify]
  );

  const addItem = useCallback(
    (product: StorefrontProduct, quantity = product.minimumOrderQuantity ?? 1) => {
      if (product.stockStatus === "out_of_stock") {
        notify("This product is out of stock.", "error");
        return;
      }
      setItems((current) => {
        const existing = current.find((item) => item.product.id === product.id);
        if (!existing) return [...current, { product, quantity }];
        const max = Math.min(product.maximumOrderQuantity ?? Infinity, product.availableQuantity ?? Infinity);
        return current.map((item) =>
          item.product.id === product.id ? { ...item, quantity: Math.min(item.quantity + quantity, max) } : item
        );
      });
      notify("Added to cart.", "success");
    },
    [notify]
  );

  const removeItem = useCallback((productId: string) => {
    setItems((current) => current.filter((item) => item.product.id !== productId));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const revalidateCart = useCallback(
    (products: StorefrontProduct[]) => {
      const available = new Map(products.map((product) => [product.id, product]));
      setItems((current) =>
        current.flatMap((item) => {
          const product = available.get(item.product.id);
          if (!product || product.stockStatus === "out_of_stock") return [];
          return [{
            product,
            quantity: Math.min(item.quantity, product.availableQuantity ?? item.quantity)
          }];
        })
      );
    },
    []
  );

  const subtotal = useMemo(() => calculateCartSubtotal(items), [items]);
  const itemCount = useMemo(() => items.reduce((count, item) => count + item.quantity, 0), [items]);

  const value = useMemo(
    () => ({
      items,
      addItem,
      removeItem,
      incrementItem: (id: string) => {
        const item = items.find((candidate) => candidate.product.id === id);
        if (item) setQuantity(id, item.quantity + 1);
      },
      decrementItem: (id: string) => {
        const item = items.find((candidate) => candidate.product.id === id);
        if (item) setQuantity(id, item.quantity - 1);
      },
      setQuantity,
      clearCart,
      revalidateCart,
      subtotal,
      itemCount
    }),
    [items, addItem, removeItem, setQuantity, clearCart, revalidateCart, subtotal, itemCount]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const value = useContext(CartContext);
  if (!value) throw new Error("useCart must be used inside CartProvider.");
  return value;
}
